import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MedicationForm, PrescriptionMedication } from "src/api/types";
import { pluralize } from "src/utils/pluralize";

const mapMedicationFormToSentence: Record<MedicationForm | string, string> = {
  injectable:
    "Inject {{amount}} ({{dosage}}) once per week for {{duration}} and discard any remaining.",
  dropper:
    "Apply {{amount}} directly to the scalp once daily on clean, dry hair. Massage gently with fingertips. Do not rinse.",
  topical:
    "Apply {{amount}} directly to the scalp once daily on clean, dry hair. Massage gently with fingertips. Do not rinse.",
  mint: "Take {{amount}} about 30 to 60 minutes before sexual activity.",
};

export const useMedicationInstructions = (
  detail: PrescriptionMedication,
  selected: number,
) => {
  const { t } = useTranslation();
  const medication = detail.medications[0];

  const amount = useMemo(() => {
    let number: string | null = null;
    let unit: string | null = null;
    if (detail.instructions[selected].amount != null) {
      [number, unit] = detail.instructions[selected].amount.split(" ");
    }

    if (number == null || unit == null) return "";

    return t(
      `medications.${medication.display_name?.toLowerCase()}.${medication.form.toLowerCase()}.instruction.amount.${unit}`,
      `{{amount}} ${unit}`,
      { amount: number },
    );
  }, [
    detail.instructions,
    medication.display_name,
    medication.form,
    selected,
    t,
  ]);

  const duration = useMemo(() => {
    const { interval, interval_count } = detail.instructions[selected];

    if (interval_count == null || interval == null) return "";
    const unit = pluralize(interval_count, interval);

    return t(
      `medications.${medication.display_name?.toLowerCase()}.${medication.form.toLowerCase()}.instruction.duration.${unit}`,
      `{{duration}} ${unit}`,
      { duration: interval_count },
    );
  }, [
    detail.instructions,
    selected,
    t,
    medication.display_name,
    medication.form,
  ]);

  const dosage = useMemo(() => {
    const { dosage } = detail.instructions[selected];
    if (dosage == null) return "";
    return dosage;
  }, [detail.instructions, selected]);

  const instruction = useMemo(() => {
    let unit: string | null = "";
    if (detail.instructions[selected].amount != null) {
      [, unit] = detail.instructions[selected].amount.split(" ");
    }

    const isMint = unit === "mint";

    const sentence =
      mapMedicationFormToSentence[isMint ? unit : medication.form];

    if (sentence != null) {
      const form = isMint ? unit : medication.form;

      return t(
        `medications.${medication.display_name?.toLowerCase()}.${form}.instruction.label`,
        sentence,
        {
          amount,
          duration,
          dosage,
        },
      );
    }

    if (detail.instructions[selected].value != null) {
      return t(
        `medications.instruction.${detail.instructions[selected].id}.label`,
        detail.instructions[selected].value,
      );
    }

    return t(
      `medications.instruction.default.label`,
      "Please reach out to your care team for instructions",
    );
  }, [
    amount,
    detail.instructions,
    dosage,
    duration,
    medication.display_name,
    medication.form,
    selected,
    t,
  ]);

  return { amount, duration, instruction };
};
